import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { Book, BookLanguage, HTMLBlock, Sentence, copyRestObject } from "../../../rest/models";
import { deepCopyObject, groupBy, orderBy } from "../../../utils";
import { getBlocksForPartialDisplay } from "../utils";

const initialState =
{
    /** A copy of the book that is loaded in the reader, on which we perform edits. */
    book: copyRestObject(new Book(), Book),
    bookLangs: [] as BookLanguage[],

    sentencesByBlock: {} as {[parent_block_id: string]: Sentence[];},
    blocksByParent: {} as {[parent_block_id: number]: HTMLBlock[]},

    translationsByLangAndSentence: {} as {[langid_sentid: string]: string},

    //Which EWRSetBookmarkButton should check the status of its request.
    sentenceForLastSetBookmarkAttempt: -1,

    pagination:
    {
        //The number of the current page. First page is 1, not 0.
        current: 1,

        //The total number of pages. Must be at least 1.
        total: 1,
        
        //The block IDs included in the current page.
        blocks: {} as {[block_id: number] : true},
        
        //The sentences included in the current page. First inclusive, last exclusive.
        sentences: {first: 0, last: 0},
    },

    //The word that the user highlighted to define with a dictionary link.
    selectedWord:
    {
        sentenceId: '',
        langId: '',
        //The span that has the word, apostrophes and all.
        wordId: -1,
        //The span inside the word with just letters.
        lettersId: -1,
    }
};

const slice = createSlice(
{
    name: 'bookReader',
    initialState,
    reducers:
    {
        init: function(state)
        {
            Object.assign(state, deepCopyObject(initialState));
        },
        setBook: function(state, action: PayloadAction<Book>)
        {
            const book = action.payload;

            state.book = book;
            state.bookLangs = orderBy(book.booklanguage_set, bl => bl.display_order);
    
            const sentences = orderBy(book.sentence_set, s => s.position);
            state.sentencesByBlock = groupBy(sentences, s => s.parent_block_id, b => b.position);
            state.blocksByParent = groupBy(book.htmlblock_set, b => b.parent_block_id, b => b.position);

            state.translationsByLangAndSentence = {};            
            const trans = state.translationsByLangAndSentence;

            for (const bl of state.book.booklanguage_set)
            {
                const lid = bl.language;
                const sentences2 = bl.translation.split('\n');
                for (let sid = 0; sid < sentences2.length; sid++)
                {
                    trans[lid + '/' + sid] = sentences2[sid];
                }
            }
            
            if (book.page_size > 0)
            {
                state.pagination.total = Math.ceil(sentences.length / book.page_size);
            }
        },

        setTranslation: function(state, action: PayloadAction<[string, number, string]>)
        {
            const book = state.book;
            if (!book) return;

            const language_id = action.payload[0];
            const sentence_id = action.payload[1];
            const value = action.payload[2];
            
            state.translationsByLangAndSentence[language_id + '/' + sentence_id] = value;
        },
        
        setBookmark: function(state, action: PayloadAction<number>)
        {
            state.book.bookmark = action.payload;
        },
        
        setSentenceForLastSetBookmarkAttempt: function(state, action: PayloadAction<number>)
        {
            state.sentenceForLastSetBookmarkAttempt = action.payload;
        },
        
        setCurrentPage: function(state, action: PayloadAction<number>)
        {
            state.pagination.current = action.payload;

            const start = (state.pagination.current - 1) * state.book.page_size;
            const end = start + state.book.page_size;

            state.pagination.sentences.first = start;
            state.pagination.sentences.last = end;

            state.pagination.blocks = getBlocksForPartialDisplay(state.book, start, end);
        },

        setSelectedWord: function(state, action: PayloadAction<typeof initialState.selectedWord>)
        {
            const sw = state.selectedWord;
            const {sentenceId, langId, wordId, lettersId} = action.payload;
            if (sw.sentenceId === sentenceId
                && sw.langId === langId
                && sw.wordId === wordId
                && sw.lettersId !== lettersId)
            {
                state.selectedWord = {sentenceId, langId, wordId, lettersId}
            }
            else
            {
                state.selectedWord = {sentenceId, langId, wordId, lettersId: -1}
            }
        },
    },
});

export const bookReaderReducer = slice.reducer;
export const bookReaderActions = slice.actions;